import {
    type ExtendedPlaceholderProductsModel,
    ListItemDiscriminator,
    type ProductListItemModel
} from 'autogen/swagger/all';
import styled, { css } from 'styled-components';
import { Headline } from 'components/generic/typography';
import { breakpoint, sizes } from 'variables';
import { Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import ProductItem from 'components/shared/productItem';
import ItemCarousel from 'components/generic/ScrollSnapSlider/components/ItemCarousel';

const Wrapper = styled.div`
    position: relative;
`;

const Header = styled(Headline)`
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.md)} {
        padding: 0;
        margin-bottom: ${Spacing24};
    }
`;

interface IProductRowProps {
    model: ExtendedPlaceholderProductsModel;
}

const StyledItemCarousel = styled(ItemCarousel)`
    width: 100%;
` as typeof ItemCarousel;

const scrollContainerStyling = css`
    && {
        padding: 0 ${Spacing16};
        scroll-padding-inline-end: ${Spacing16};
        scroll-padding-inline-start: ${Spacing16};
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: minmax(194px, 0.5fr);
        gap: ${Spacing16};

        ${breakpoint.up(sizes.sm)} {
            grid-auto-columns: minmax(220px, 0.5fr);
            gap: ${Spacing24};
        }

        ${breakpoint.up(sizes.xl)} {
            scroll-padding-inline-end: 0;
            scroll-padding-inline-start: 0;
            padding: 0;
        }

        ${breakpoint.up(sizes.xxl)} {
            grid-auto-columns: minmax(254px, 0.25fr);
        }
    }
`;

const CarouselOverflowContainer = styled.div`
    position: relative;
    margin: 0 -${Spacing16};

    ${breakpoint.up(sizes.xl)} {
        margin: 0;
    }
`;

function SliderItems({ items }: { items: ExtendedPlaceholderProductsModel['items'] }) {
    return (
        <>
            {items.map((item, index) =>
                ListItemDiscriminator(
                    item,
                    () => null,
                    () => null,
                    (model) => <ProductItem model={model} key={model.id} data-slide-index={index} />,
                    () => {
                        throw new Error('API mismatch - ProductRow');
                    }
                )
            )}
        </>
    );
}

export default function ProductRow(props: IProductRowProps): JSX.Element {
    const {
        model: { items, heading }
    } = props;

    return (
        <Wrapper>
            {heading && <Header size="L">{heading}</Header>}
            <CarouselOverflowContainer>
                <StyledItemCarousel
                    scrollContainerCss={scrollContainerStyling}
                    items={items}
                    itemRenderer={(item, index) => (
                        <ProductItem model={item as ProductListItemModel} key={index} data-slide-index={index} />
                    )}
                />
            </CarouselOverflowContainer>
        </Wrapper>
    );
}
